import type { FacilityActionPayload, RoomState } from '../../shared/types.js';
import type { ControllerNetwork } from '../controllerNetwork.js';
import { state as controllerState } from '../controllerState.js';
import { buildTechTreeIndex, type TechTreeData, type TechTreeIndex } from '../../shared/techTree.js';
import { htmlEl } from './stationDisplays.js';
import { createChipTabs, createFacilityHead, createSearchBar } from './facilityUi.js';
import { matchesSearchText } from '../../shared/shopFilter.js';

type Options = {
  controls: HTMLDivElement | null;
  getControllerNetwork: () => ControllerNetwork | null;
};

type Upgrade = { id: string; name: string; desc: string; tier: number; price: number; category: string };

let techIndex: TechTreeIndex | null = null;
let techLoadStarted = false;

const loadTechIndex = (onReady: () => void): void => {
  if (techIndex) { onReady(); return; }
  if (techLoadStarted) return;
  techLoadStarted = true;
  fetch('/config/tech-tree-data.json')
    .then((res) => res.json() as Promise<TechTreeData>)
    .then((data) => { techIndex = buildTechTreeIndex(data); onReady(); })
    .catch(() => { techLoadStarted = false; });
};

const CATEGORY_LABELS: Record<string, string> = {
  ship_systems: 'Ship Systems',
  propulsion: 'Propulsion',
  drones: 'Drones',
  hull: 'Hull',
  life_support: 'Life Support'
};

export const createFacilityUpgradesController = ({ controls, getControllerNetwork }: Options) => {
  let list: HTMLDivElement | null = null;
  let setCredits: ((value: number) => void) | null = null;
  let filter = 'all';
  let searchText = '';
  let builtOwnedSignature = '';
  let tabs: ReturnType<typeof createChipTabs<string>> | null = null;

  const emit = (payload: FacilityActionPayload): void => {
    getControllerNetwork()?.facilityAction(payload);
  };

  const collect = (roomState: RoomState): Map<string, Upgrade[]> => {
    const byCategory = new Map<string, Upgrade[]>();
    if (!techIndex) return byCategory;
    for (const [id, def] of Object.entries(techIndex.data.products)) {
      if (def.kind !== 'upgrade') continue;
      const price = roomState.facilityPrices[id];
      if (price === undefined) continue;
      const category = def.category ?? 'other';
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category)!.push({ id, name: def.name, desc: def.desc, tier: def.tier, price, category });
    }
    for (const items of byCategory.values()) items.sort((a, b) => a.tier - b.tier);
    return byCategory;
  };

  const buildCard = (item: Upgrade, roomState: RoomState): HTMLElement => {
    const owned = roomState.ownedUpgrades.includes(item.id);
    const card = htmlEl('div', `facility-item-row fc-card${owned ? ' owned' : ''}`);
    card.dataset.itemId = item.id;

    const top = htmlEl('div', 'fc-card-top');
    top.append(htmlEl('span', `facility-tier-badge tier-${item.tier}`, `T${item.tier}`), htmlEl('span', 'fc-card-name', item.name));
    const desc = htmlEl('div', 'fc-card-desc', item.desc);

    const foot = htmlEl('div', 'fc-card-foot');
    if (owned) {
      foot.append(htmlEl('span', 'facility-owned-label', '✓ INSTALLED'));
    } else {
      const price = htmlEl('div', 'facility-price fc-price', `${item.price.toLocaleString()} CR`);
      const short = htmlEl('div', 'fc-short');
      const btn = htmlEl('button', 'facility-buy-btn', 'Purchase');
      btn.type = 'button';
      btn.dataset.itemId = item.id;
      btn.addEventListener('click', () => {
        const code = controllerState.currentRoomCode;
        if (code) emit({ code, type: 'purchase_upgrade', itemId: item.id });
      });
      const priceBox = htmlEl('div', 'fc-price-box');
      priceBox.append(price, short);
      foot.append(priceBox, btn);
    }
    card.append(top, desc, foot);
    return card;
  };

  const buildList = (roomState: RoomState): void => {
    if (!list || !techIndex) return;
    list.innerHTML = '';
    builtOwnedSignature = roomState.ownedUpgrades.join('|');
    const byCategory = collect(roomState);

    for (const [category, items] of byCategory) {
      const searching = searchText.trim() !== '';
      if (!searching && filter !== 'all' && filter !== category) continue;
      const matching = searching ? items.filter((item) => matchesSearchText(`${item.name} ${item.desc}`, searchText)) : items;
      if (matching.length === 0) continue;
      const installed = items.filter((item) => roomState.ownedUpgrades.includes(item.id)).length;
      const section = htmlEl('section', 'fc-section');
      const header = htmlEl('div', 'fc-section-head');
      header.append(htmlEl('span', 'fc-section-title', CATEGORY_LABELS[category] ?? category), htmlEl('span', 'fc-section-count', `${installed}/${items.length} installed`));
      const grid = htmlEl('div', 'fc-grid');
      const ordered = [...matching].sort((a, b) => Number(roomState.ownedUpgrades.includes(a.id)) - Number(roomState.ownedUpgrades.includes(b.id)));
      for (const item of ordered) grid.append(buildCard(item, roomState));
      section.append(header, grid);
      list.append(section);
    }
    refreshStates(roomState);
  };

  const refreshStates = (roomState: RoomState): void => {
    if (!list) return;
    const credits = roomState.credits;
    for (const card of list.querySelectorAll<HTMLElement>('.fc-card')) {
      const id = card.dataset.itemId;
      if (!id) continue;
      const price = roomState.facilityPrices[id];
      const owned = roomState.ownedUpgrades.includes(id);
      card.classList.toggle('owned', owned);
      const btn = card.querySelector<HTMLButtonElement>('.facility-buy-btn');
      const priceEl = card.querySelector<HTMLElement>('.facility-price');
      const short = card.querySelector<HTMLElement>('.fc-short');
      if (btn && price !== undefined) {
        btn.disabled = owned || credits < price;
        card.classList.toggle('affordable', !owned && credits >= price);
        priceEl?.classList.toggle('unaffordable', credits < price);
        if (short) short.textContent = credits < price ? `need ${(price - credits).toLocaleString()} more` : '';
      }
    }
  };

  const render = (): void => {
    if (!controls) return;
    controls.innerHTML = '';
    controls.classList.add('facility-role-layout');

    const head = createFacilityHead('SHIP UPGRADES');
    setCredits = head.setCredits;
    head.setCredits(controllerState.lastRoomState?.credits ?? 0);

    tabs = createChipTabs<string>({
      tabs: [{ id: 'all', label: 'All' }, ...Object.entries(CATEGORY_LABELS).map(([id, label]) => ({ id, label }))],
      active: filter,
      onChange: (id) => {
        filter = id;
        const roomState = controllerState.lastRoomState;
        if (roomState) buildList(roomState);
      }
    });

    const search = createSearchBar('Search upgrades…', (text) => {
      searchText = text;
      const roomState = controllerState.lastRoomState;
      if (roomState) buildList(roomState);
    });
    search.input.value = searchText;
    list = htmlEl('div', 'fc-list');
    controls.append(head.root, tabs.root, search.root, list);

    const roomState = controllerState.lastRoomState;
    if (roomState) {
      loadTechIndex(() => buildList(roomState));
    }
  };

  const updatePanel = (): void => {
    const roomState = controllerState.lastRoomState;
    if (!roomState || !list || !techIndex) return;
    setCredits?.(roomState.credits);
    if (roomState.ownedUpgrades.join('|') !== builtOwnedSignature) {
      buildList(roomState);
      return;
    }
    refreshStates(roomState);
  };

  return { render, updatePanel };
};
